
import { useEffect, useState, Suspense } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCanvas } from '@/hooks/useCanvas'
import { useAuth } from '@/hooks/useAuth'
import { FullScreenLoader } from '@/components/ui/FullScreenLoader'
import { usePostHog } from 'posthog-js/react'

function NewCanvasContent() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { projectId?: string }
  const projectId = search.projectId
  const { createCanvas } = useCanvas()
  const { isAuthenticated, isLoading: authLoading, user } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const posthog = usePostHog()

  useEffect(() => {
    async function createNewCanvas() {
      // Wait for auth to finish loading
      if (authLoading) return

      // Redirect to login if not authenticated
      if (!isAuthenticated || !user) {
        navigate({ to: '/login' })
        return
      }

      try {
        // Create canvas via API
        const newCanvas = await createCanvas({
          title: 'Untitled', // API will generate unique slug like "untitled-1"
          projectId: projectId || undefined,
          isPublic: false
          // nodes, edges, config will use default values from schema
        })

        if (newCanvas) {
          posthog.capture('canvas_created', {
            canvas_id: newCanvas.uuid,
            canvas_title: newCanvas.title,
            has_project: !!projectId,
          })
          // Redirect to the new canvas page
          navigate({ to: `/canvas/${newCanvas.uuid}`, replace: true })
        } else {
          throw new Error('Failed to create session')
        }
      } catch (err) {
        posthog.captureException(err instanceof Error ? err : new Error('Failed to create session'))
        console.error('Failed to create session:', err)
        setError(err instanceof Error ? err.message : 'Failed to create session')
      }
    }

    createNewCanvas()
  }, [projectId, navigate, createCanvas, isAuthenticated, authLoading, user, posthog])

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-destructive">
            <svg className="mx-auto h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-destructive mb-4">{error}</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-muted text-muted-foreground rounded hover:bg-accent"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return <FullScreenLoader />
}

export function NewCanvasPage() {
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <NewCanvasContent />
    </Suspense>
  )
}
